import { http } from "../../core/http";
import type {
  CreateOrderPayload,
  ReviewOrderCostPayload,
  UpdateOrderStagePeriodPayload,
  UpdateOrderPayload,
} from "./types";

type OrderRequestPayload = Record<string, unknown>;
type OrderQueryParams = Record<string, unknown>;

// Axios responses here remain dynamic; the default preserves existing callers.
type OrderApiResponse<T = any> = {
  success?: boolean;
  message?: string;
  data?: T;
  totalDocs?: number;
  limit?: number;
  page?: number;
  totalItem?: number;
  pageSize?: number;
  [key: string]: unknown;
};

const assertOrderApiSuccess = <T = any>(response: OrderApiResponse<T>) => {
  if (response?.success === false) {
    throw new Error(response?.message || "Order request failed");
  }
  return response;
};

const getAllOrder = async (params: OrderQueryParams) => {
  const response = await http
    .get("admin/orders", { params })
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const postAddOrder = async (payload: CreateOrderPayload) => {
  const response = await http
    .post("/admin/orders", payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const putUpdateOrder = async (id: string, payload: UpdateOrderPayload) => {
  const response = await http
    .put(`admin/orders/${id}`, payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const putRejectOrderShipping = async (id: string) => {
  const response = await http
    .put(`admin/orders/${id}/reject-shipping`, {})
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const putUpdateOrderStatus = async (
  id: string,
  payload: { status: string },
) => {
  const response = await http
    .put(`admin/orders/${id}/status`, payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const patchOrder = async (id: string, payload: OrderRequestPayload) => {
  const response = await http
    .patch(`admin/orders/${id}`, payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const putUpdateOrderStagePeriod = async (
  payload: UpdateOrderStagePeriodPayload,
) => {
  const response = await http
    .put("admin/orders/order-stage-period", payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const deleteOrder = async (id: string) => {
  const response = await http
    .delete(`admin/orders/${id}`)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const getOrderCostSheet = async (id: string) => {
  const response = await http
    .get(`admin/orders/${id}`)
    .then((res) => res.data);
  return assertOrderApiSuccess(response).data;
};

const patchUpdateOrderCost = async (
  id: string,
  payload: OrderRequestPayload,
) => {
  const response = await http
    .patch(`admin/orders/${id}/cost-sheet/operator`, payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response).data;
};

const postAllocateOrderCost = async (payload: {
  from: string;
  to: string;
}) => {
  const response = await http
    .post("admin/orders/allocate-cost", payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const putSubmitOrderCost = async (
  id: string,
  level: 1 | 2,
  payload?: OrderRequestPayload,
) => {
  const response = await http
    .put(`admin/orders/${id}/cost-sheet/submit-l${level}`, payload ?? {})
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

const patchReviewOrderCost = async (
  id: string,
  level: 1 | 2,
  payload: ReviewOrderCostPayload,
) => {
  const response = await http
    .patch(`admin/orders/${id}/cost-sheet/review-l${level}`, payload)
    .then((res) => res.data);
  return assertOrderApiSuccess(response);
};

export default {
  getAllOrder,
  postAddOrder,
  putUpdateOrder,
  putRejectOrderShipping,
  putUpdateOrderStatus,
  patchOrder,
  putUpdateOrderStagePeriod,
  deleteOrder,
  getOrderCostSheet,
  patchUpdateOrderCost,
  postAllocateOrderCost,
  putSubmitOrderCost,
  patchReviewOrderCost,
};
